/**
 * Ability icon resolver. Mirrors the build-menu `BUILDING_ICON_ASSET` pattern:
 * a data map from AbilityId → public asset path, plus a resolver that falls back
 * to a colour-tinted SVG so a missing/unbound slot never 404s.
 *
 * Art: V Rising-style painterly glowing-energy icons, colour-coded by ability
 * TYPE (frost=blue, heal=green, martial=red, arcane=violet, …). Square dark-bg
 * PNGs under `public/assets/abilities/<AbilityId>.png` — the plate's circular
 * `.mp-skill-icon` clips them to a disc; the tavern shop shows them square.
 * Generated 2026-06-23 (spec: docs/superpowers/specs/2026-06-23-ability-icons-v1-design.md).
 *
 * `ACCENT[id]` is the type colour — reused for the cooldown-tint fallback and
 * available to any HUD surface that wants the ability's hue.
 */
import type { AbilityId } from "@tribez/shared";

/** Per-ability type colour (the icon's dominant hue). */
export const ACCENT: Record<AbilityId, string> = {
  // Earth / Mining — amber
  MineralTap: "#e0962e",
  AdrenalineRush: "#e0962e",
  // Nature / Growth — leaf green
  NaturesGrace: "#5fbf3a",
  PlantTree: "#5fbf3a",
  SageMode: "#5fbf3a",
  // Heal / Restore — green
  CelestialHymn: "#5fe06a",
  GuardianSpirit: "#5fe06a",
  // Holy / Ward — gold-white
  Clairvoyance: "#ffe08a",
  Stance: "#ffe08a",
  PowerWordShield: "#ffe08a",
  // Frost — icy blue
  FrostArmor: "#5ac8ff",
  // Dispel — pale cyan
  NeutralizingWave: "#88ccff",
  // Arcane / Void — violet
  BlackHole: "#b65cff",
  ArcaneNova: "#b65cff",
  SpectralEdict: "#b65cff",
  // Shadow / Drain — indigo
  SiphonLife: "#8a6cff",
  ShadowCloak: "#8a6cff",
  // Blight / Curse — toxic green
  Affliction: "#9bd13a",
  // Martial / Blood — crimson
  Backstab: "#ff4242",
  Flurry: "#ff4242",
  Warcry: "#ff4242",
};

/** AbilityId → real PNG asset under client/public. To add/replace art, drop the
 *  file and (if new) add the entry. All 21 v1 abilities ship art. */
export const ABILITY_ICON_ASSET: Partial<Record<AbilityId, string>> = Object.fromEntries(
  (Object.keys(ACCENT) as AbilityId[]).map((id) => [id, `/assets/abilities/${id}.png`]),
) as Partial<Record<AbilityId, string>>;

/** Tinted SVG fallback so an unmapped/unknown ability never 404s — a soft radial
 *  glow in the ability's accent colour (or neutral grey if unknown). */
function fallbackUrl(id: AbilityId): string {
  const c = ACCENT[id] ?? "#6a6a6a";
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'>` +
    `<rect width='64' height='64' fill='#14131a'/>` +
    `<circle cx='32' cy='32' r='20' fill='${c}' opacity='0.55'/>` +
    `</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** Icon URL for an ability — real art if present, else a tinted fallback. */
export function abilityIconUrl(id: AbilityId): string {
  return ABILITY_ICON_ASSET[id] ?? fallbackUrl(id);
}
